import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FlaskConical, Leaf, Trash2 } from 'lucide-react';
import type { BlendHistoryEntry } from '@shared';
import { blendsApi } from '../lib/blendsApi';
import {
  EmptyState,
  ErrorState,
  LoadingState,
  PrimaryLink,
  SearchBar,
} from '../components/ui';

type Tab = 'saved' | 'recent';

/**
 * History (`/history`): saved blend calculations from the server history API.
 */
export function Dashboard(): JSX.Element {
  const [tab, setTab] = useState<Tab>('saved');
  const [entries, setEntries] = useState<BlendHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    blendsApi
      .history()
      .then(setEntries)
      .catch((err: unknown) =>
        setError(
          err instanceof Error ? err.message : 'Could not load blend history.',
        ),
      )
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const remove = async (id: string): Promise<void> => {
    if (!window.confirm('Remove this saved blend from history?')) return;
    setBusyId(id);
    try {
      await blendsApi.deleteHistory(id);
      setEntries((prev) => prev.filter((e) => e.id !== id));
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Could not delete history entry.',
      );
    } finally {
      setBusyId(null);
    }
  };

  const filtered = entries.filter((e) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    const names = e.request.strains.map((s) => s.name).join(' ');
    const note = e.note ?? '';
    return `${names} ${note}`.toLowerCase().includes(q);
  });

  const list =
    tab === 'recent'
      ? [...filtered].sort(
          (a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt),
        )
      : filtered;

  return (
    <div className="stack">
      <div>
        <h1 className="page-title">
          My Blends
          <Leaf
            className="page-title__leaf"
            size={22}
            strokeWidth={1.75}
            aria-hidden
          />
        </h1>
        <p className="page-lead">Saved calculations from this device.</p>
      </div>

      <div className="segmented" role="tablist" aria-label="History views">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'saved'}
          className={tab === 'saved' ? 'is-active' : ''}
          onClick={() => setTab('saved')}
        >
          Saved Blends
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'recent'}
          className={tab === 'recent' ? 'is-active' : ''}
          onClick={() => setTab('recent')}
        >
          Recent
        </button>
      </div>

      <SearchBar
        value={search}
        onChange={setSearch}
        placeholder="Search blends…"
      />

      {loading && <LoadingState label="Loading history…" />}
      {error && <ErrorState message={error} />}

      {!loading && !error && list.length === 0 && (
        <EmptyState
          title="No saved blends yet"
          description="Saved blends from earlier sessions will appear here."
          action={
            <PrimaryLink to="/calculator" icon={<FlaskConical size={18} />}>
              Open Blend Calculator
            </PrimaryLink>
          }
        />
      )}

      <div className="stack" style={{ gap: '0.75rem' }}>
        {list.map((e) => {
          const names = e.request.strains.map((s) => s.name).join(' + ');
          const title =
            e.note?.trim() ||
            (names.length > 48 ? `${names.slice(0, 45)}…` : names) ||
            'Untitled blend';
          const ratio = e.result.allocations
            .map((a) => {
              const pct =
                e.result.totalWeightGrams > 0
                  ? Math.round((a.grams / e.result.totalWeightGrams) * 100)
                  : 0;
              return `${pct}%`;
            })
            .join(' / ');

          return (
            <article key={e.id} className="card blend-history-card">
              <div className="spread" style={{ alignItems: 'flex-start' }}>
                <div>
                  <h2 className="blend-history-card__title">{title}</h2>
                  <div className="muted" style={{ fontSize: '0.85rem' }}>
                    {names}
                  </div>
                </div>
                <button
                  type="button"
                  className="icon-btn"
                  aria-label="Delete blend"
                  disabled={busyId === e.id}
                  onClick={() => void remove(e.id)}
                >
                  <Trash2 size={16} strokeWidth={2} />
                </button>
              </div>
              <div className="blend-history-card__stats">
                <span>{e.result.estimatedThcMg.toFixed(0)} mg THC</span>
                <span>{e.request.totalWeightGrams} g</span>
                <span>{ratio}</span>
              </div>
              <div className="muted" style={{ fontSize: '0.8rem' }}>
                {new Date(e.createdAt).toLocaleString()}
              </div>
              <Link
                to="/calculator"
                className="btn btn-secondary"
                style={{ alignSelf: 'flex-start' }}
              >
                New calculation
              </Link>
            </article>
          );
        })}
      </div>

      <Link to="/calculator" className="fab" aria-label="New blend">
        <FlaskConical size={22} strokeWidth={2.25} />
      </Link>
    </div>
  );
}
